import { Component } from '@theme/component';

/**
 * A block of text truncated to fit on a single line, with a trailing
 * "Read More" / "Read Less" control inline at the end of that line.
 *
 * Uses `white-space: nowrap` + measured overflow instead of CSS
 * `-webkit-line-clamp`: line-clamp applies its own ellipsis and silently
 * clips whatever follows once the text is long enough to overflow, which
 * hides the toggle control entirely rather than shortening the text to
 * make room for it. Re-measures on resize since the available width
 * (and therefore how much text fits) changes across breakpoints.
 *
 * @extends {Component<{row: HTMLElement, text: HTMLElement, toggle: HTMLElement}>}
 */
class ReadMoreTextComponent extends Component {
  requiredRefs = ['row', 'text', 'toggle'];

  /** @type {string} */
  #fullHTML = '';

  /** @type {string} */
  #plainText = '';

  /** @type {string} */
  #truncatedText = '';

  #expanded = false;

  /** @type {Animation | null} */
  #heightAnimation = null;

  /** @type {number} */
  #lastWidth = 0;

  /* Only a width change can alter how much text fits on the line, so the
     observer bails on height-only callbacks. That also keeps it quiet
     during the expand/collapse animation below, which changes height every
     frame and would otherwise re-run the binary-search truncation on each
     one for an identical result. */
  #resizeObserver = new ResizeObserver((entries) => {
    const width = entries[0]?.contentRect.width ?? 0;
    if (width === this.#lastWidth) return;
    this.#lastWidth = width;
    if (!this.#expanded) this.#renderCollapsed();
  });

  connectedCallback() {
    super.connectedCallback();

    this.#fullHTML = this.refs.text.innerHTML;

    const measure = document.createElement('div');
    measure.innerHTML = this.#fullHTML.replace(/<br\s*\/?>/gi, ' ');
    this.#plainText = measure.textContent?.replace(/\s+/g, ' ').trim() ?? '';

    this.#renderCollapsed();
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#heightAnimation?.cancel();
  }

  toggleExpanded() {
    // Measured before the content swap below, which resizes the element
    // synchronously - afterwards the old height is unrecoverable.
    const startHeight = this.getBoundingClientRect().height;

    this.#expanded = !this.#expanded;
    this.classList.toggle('is-expanded', this.#expanded);
    this.refs.toggle.setAttribute('aria-expanded', String(this.#expanded));
    this.refs.text.innerHTML = this.#expanded ? this.#fullHTML : this.#truncatedText;

    this.#animateHeight(startHeight);
  }

  /**
   * Client feedback: the expand/collapse used to jump straight to its end
   * state, because toggling `.is-expanded` flips white-space/overflow and
   * swaps the text in one synchronous step. There is no single property to
   * transition across that, so the height is measured either side of the
   * swap and animated between the two.
   * @param {number} startHeight - height in px before the content swap
   */
  #animateHeight(startHeight) {
    this.#heightAnimation?.cancel();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const endHeight = this.getBoundingClientRect().height;
    if (Math.abs(endHeight - startHeight) < 1) return;

    // The text is already at its final length, so on expand it would spill
    // past the animating box without this.
    this.classList.add('is-animating');

    const animation = this.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      { duration: 360, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)' }
    );
    this.#heightAnimation = animation;

    animation.finished
      .catch(() => {
        /* cancel() rejects the promise - nothing to handle */
      })
      .finally(() => {
        // A rapid re-toggle cancels this animation and starts another in the
        // same tick; without this check the cancelled one's cleanup would
        // run afterwards and strip the class off its replacement.
        if (this.#heightAnimation === animation) this.classList.remove('is-animating');
      });
  }

  #renderCollapsed() {
    this.#truncatedText = this.#computeTruncatedText();
    this.refs.text.textContent = this.#truncatedText;
    this.refs.toggle.hidden = this.#truncatedText === this.#plainText;
  }

  /**
   * Binary-searches the longest word-boundary prefix of the full text that
   * still fits on one line alongside the toggle control, appending an
   * ellipsis when trimmed. Returns the untrimmed text as-is if it already
   * fits with no truncation needed.
   * @returns {string}
   */
  #computeTruncatedText() {
    const { text } = this.refs;
    const words = this.#plainText.split(' ');

    text.textContent = this.#plainText;
    if (!this.#overflows()) return this.#plainText;

    let lo = 0;
    let hi = words.length;

    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      text.textContent = `${words.slice(0, mid).join(' ')}…`;
      if (this.#overflows()) {
        hi = mid - 1;
      } else {
        lo = mid;
      }
    }

    return `${words.slice(0, lo).join(' ')}…`;
  }

  #overflows() {
    const { row } = this.refs;
    return row.scrollWidth > row.clientWidth + 1;
  }
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreTextComponent);
}
